import { SubscriptionStatusBadge } from '@/components/SubscriptionStatusBadge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  describeRecordState,
  HISTORY_NO_STATUS_CHANGE,
  setStatusChange,
  statusChangeFor,
  statusOptionsFor,
  statusOptionValue,
  statusTakesDate,
  statusTakesFinalSequence
} from '@/lib/history'
import type { Customer, HistoryWorklistEntry } from '@/api/types'

interface HistoryStatusCardProps {
  customer: Customer
  entry: HistoryWorklistEntry
  isDisabled: boolean
  onChange: (entry: HistoryWorklistEntry) => void
}

export function HistoryStatusCard({ customer, entry, isDisabled, onChange }: HistoryStatusCardProps): React.ReactElement {
  const subscription = customer.subscription
  const status = entry.status
  const options = statusOptionsFor(subscription?.status, status)
  const value = statusOptionValue(status)
  const takesDate = statusTakesDate(status?.to)
  const takesFinal = statusTakesFinalSequence(status?.to)
  const finalText = subscription?.final_sequence ? ` · Sista leverans ${subscription.final_sequence}` : ''

  const handleTargetChange = (next: string): void => {
    const option = options.find((candidate) => candidate.value === next)

    if (!option) {
      onChange(setStatusChange(entry, null))

      return
    }

    const finalSequence = statusTakesFinalSequence(option.to) ? (status?.finalSequence ?? entry.recordState.lastDelivery ?? 1) : null

    onChange(setStatusChange(entry, statusChangeFor(option.to, status?.since ?? null, finalSequence)))
  }

  const handleSinceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!status) return

    onChange(setStatusChange(entry, { ...status, since: event.target.value === '' ? null : event.target.value }))
  }

  const handleFinalChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!status) return

    const finalSequence = Number(event.target.value)

    if (Number.isInteger(finalSequence) && finalSequence > 0) onChange(setStatusChange(entry, { ...status, finalSequence }))
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>1. Abonnemangets läge</CardTitle>
        <CardDescription>Avstämningen säger: {describeRecordState(entry)}.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm md:grid-cols-2">
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">I plattformen nu</p>
          <div className="flex flex-wrap items-center gap-2">
            <SubscriptionStatusBadge subscription={subscription} />
            <span className="text-muted-foreground">{finalText}</span>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sätts till vid inläsningen</p>
          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_10rem_5rem]">
            <Select
              value={value}
              disabled={isDisabled}
              onValueChange={handleTargetChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={HISTORY_NO_STATUS_CHANGE}>Ingen ändring</SelectItem>
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {status && takesDate && (
              <Input
                type="date"
                value={status.since ?? ''}
                disabled={isDisabled}
                onChange={handleSinceChange}
                aria-label="Sedan"
              />
            )}
            {takesFinal && (
              <Input
                type="number"
                min={1}
                value={status?.finalSequence ?? 1}
                disabled={isDisabled}
                onChange={handleFinalChange}
                aria-label="Sista leverans"
                placeholder="Sista"
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Ingen ändring = kunden förblir som i plattformen. Datumet är dagen det hände. Aktiv igen: kunden står som avslutad, uppsagd
            eller ångrad men fortsätter; avslutets datum och sista leverans tas bort och motorn bokar nästa leverans som vanligt, om
            leveranserna är på. Ånger med leveranser: kunden behåller och betalar till och med det numret på sina vanliga datum. Uppsagd med
            sista leverans: leveranserna före går på sina datum, den sista skickas direkt när den föregående kommit fram. Det går också från
            avslutad, för en kund som avslutades för tidigt.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
